// ─── Écran de connexion (Core) ────────────────────────────────────
// Base de comptes COMMUNE à la suite : on se connecte avec le même compte que
// MoPCal. Écran volontairement minimal (e-mail + mot de passe).
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUI, useBasculerTheme } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { useAuth } from './AuthContext';
import { connexionGoogleDisponible, connexionAppleDisponible } from './connexionTierceService';
import { APP_NAME } from '../version';

// Modules natifs (require protégé dans connexionTierceService) : un binaire
// pas encore migré ne doit voir apparaître ni bouton ni erreur.
const AVEC_GOOGLE = Platform.OS !== 'web' && connexionGoogleDisponible();
const AVEC_APPLE  = Platform.OS === 'ios' && connexionAppleDisponible();

export default function EcranConnexion({ nomApp = APP_NAME }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const insets = useSafeAreaInsets();
  const basculer = useBasculerTheme();
  const { connexion, inscription, connexionGoogle, connexionApple } = useAuth();

  const [email, setEmail] = useState('');
  const [mdp, setMdp]     = useState('');
  const [voir, setVoir]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  async function faire(fn) {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr(e?.message || String(e)); } finally { setBusy(false); }
  }

  return (
    <View style={[s.page, { paddingTop: insets.top }]}>
      <TouchableOpacity style={s.theme} onPress={basculer}><Text style={s.themeTxt}>🌗</Text></TouchableOpacity>
      <View style={s.centre}>
        <Text style={s.logo}>🧩</Text>
        <Text style={s.h1}>{nomApp}</Text>
        <Text style={s.sub}>{t('login.sousTitreSuite')}</Text>
        <View style={s.carte}>
          <TextInput style={s.input} value={email} onChangeText={setEmail} placeholder={t('login.phEmail')}
            placeholderTextColor={UI.faint} autoCapitalize="none" keyboardType="email-address" />
          <View style={s.champMdp}>
            <TextInput style={s.inputMdp} value={mdp} onChangeText={setMdp} placeholder={t('login.phMotDePasse')}
              placeholderTextColor={UI.faint} secureTextEntry={!voir} autoCapitalize="none" autoCorrect={false} />
            <TouchableOpacity style={s.oeil} onPress={() => setVoir(v => !v)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              accessibilityLabel={voir ? t('login.masquerMdp') : t('login.afficherMdp')}>
              <Text style={s.oeilTxt}>{voir ? '🙈' : '👁️'}</Text>
            </TouchableOpacity>
          </View>
          {!!err && <Text style={s.err}>{err}</Text>}
          <TouchableOpacity style={[s.btn, busy && { opacity: 0.6 }]} disabled={busy}
            onPress={() => faire(() => connexion(email, mdp))}>
            <Text style={s.btnTxt}>{busy ? '…' : t('login.seConnecter')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.btn, s.btnAlt]} disabled={busy}
            onPress={() => faire(() => inscription(email, mdp))}>
            <Text style={[s.btnTxt, s.btnAltTxt]}>{t('login.creerCompte')}</Text>
          </TouchableOpacity>
          {(AVEC_GOOGLE || AVEC_APPLE) && (
            <View style={s.separateur}>
              <View style={s.trait} /><Text style={s.ouTxt}>{t('login.ou')}</Text><View style={s.trait} />
            </View>
          )}
          {AVEC_GOOGLE && (
            <TouchableOpacity style={[s.btn, s.btnTiers]} disabled={busy}
              onPress={() => faire(connexionGoogle)}>
              <Text style={s.btnTiersTxt}>🔵 {t('login.continuerGoogle')}</Text>
            </TouchableOpacity>
          )}
          {AVEC_APPLE && (
            <TouchableOpacity style={[s.btn, s.btnTiers, s.btnApple]} disabled={busy}
              onPress={() => faire(connexionApple)}>
              <Text style={[s.btnTiersTxt, s.btnAppleTxt]}> {t('login.continuerApple')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:   { flex: 1, backgroundColor: UI.bg },
  theme:  { alignSelf: 'flex-end', padding: 14 },
  themeTxt: { fontSize: 18 },
  centre: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 22, marginTop: -40 },
  logo:   { fontSize: 44, marginBottom: 6 },
  h1:     { fontSize: 28, fontWeight: '800', color: UI.ink },
  sub:    { fontSize: 14, color: UI.muted, marginTop: 4, textAlign: 'center' },
  carte:  { width: '100%', maxWidth: 380, backgroundColor: UI.card, borderRadius: 16,
            borderWidth: 1, borderColor: UI.border, padding: 18, marginTop: 20, gap: 10 },
  input:  { backgroundColor: UI.champ, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12,
            fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  champMdp: { flexDirection: 'row', alignItems: 'center', backgroundColor: UI.champ, borderRadius: 12,
              borderWidth: 0.5, borderColor: UI.champBord },
  inputMdp: { flex: 1, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, color: UI.ink },
  oeil:    { paddingHorizontal: 12, paddingVertical: 10 },
  oeilTxt: { fontSize: 18 },
  btn:    { backgroundColor: UI.accent, borderRadius: 12, paddingVertical: 13, alignItems: 'center' },
  btnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
  btnAlt: { backgroundColor: UI.card, borderWidth: 1, borderColor: UI.border },
  btnAltTxt: { color: UI.accent },
  err:    { color: UI.danger, fontSize: 13 },
  separateur: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  trait:  { flex: 1, height: 1, backgroundColor: UI.border },
  ouTxt:  { fontSize: 12, color: UI.faint, fontWeight: '700' },
  btnTiers: { backgroundColor: UI.card, borderWidth: 1, borderColor: UI.border },
  btnTiersTxt: { color: UI.ink, fontWeight: '700', fontSize: 15 },
  btnApple: { backgroundColor: '#000' },
  btnAppleTxt: { color: '#fff' },
});
